import {
  IconFile,
  IconFileTypePdf,
  IconFileTypeDocx,
  IconFileTypeXls,
  IconPhoto,
} from "@tabler/icons-react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import type { Attachment } from "@/types/Attachment";

/**
 * The document templates an attachment really delivers. Only a
 * DOCUMENT_BUILDER or MIXED attachment renders its templates (mail, portal
 * and Java alike); an UPLOAD attachment keeps the ids it had before a source
 * switch — the backend update leaves them on the document — and must not
 * count them, or the cockpit promises files nobody ever gets.
 *
 * A repeated id is one document (first-seen order kept), as in the portal
 * Worker. Editors start from this list too, so a save never writes the
 * leftovers back.
 */
export function deliveredTemplateIds(
  att: Pick<Attachment, "sourceType" | "documentBuilderTemplateIds">,
): string[] {
  if (att.sourceType !== "DOCUMENT_BUILDER" && att.sourceType !== "MIXED") return [];
  return [...new Set(att.documentBuilderTemplateIds ?? [])];
}

/**
 * Shared attachment file-type taxonomy + display helpers. Extracted from the
 * email builder's AttachmentsPanel so the send-email attachment preview renders
 * identical icons/colours/thumbnails. Keep the kind mapping in sync across both.
 */
export type FileKind = "image" | "pdf" | "word" | "excel" | "other";

export const FILE_KIND_STYLE: Record<
  FileKind,
  { Icon: TablerIcon; color: string }
> = {
  image: { Icon: IconPhoto, color: "text-emerald-500" },
  pdf: { Icon: IconFileTypePdf, color: "text-rose-500" },
  word: { Icon: IconFileTypeDocx, color: "text-blue-500" },
  excel: { Icon: IconFileTypeXls, color: "text-green-600" },
  other: { Icon: IconFile, color: "font-tertiary" },
};

export function getFileKind(fileName: string, contentType?: string): FileKind {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  const ct = (contentType ?? "").toLowerCase();
  if (
    ct.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)
  )
    return "image";
  if (ct === "application/pdf" || ext === "pdf") return "pdf";
  if (ct.includes("word") || ["doc", "docx"].includes(ext)) return "word";
  if (
    ct.includes("excel") ||
    ct.includes("spreadsheet") ||
    ["xls", "xlsx", "csv"].includes(ext)
  )
    return "excel";
  return "other";
}

// Public-asset host (same bucket the presigned PUT targets). Lets us render a
// real thumbnail for already-persisted image files via `${BASE}/${s3Key}`.
const ASSET_BASE = import.meta.env.VITE_ASSET_BASE_URL as string | undefined;

export function buildAssetUrl(s3Key?: string): string | null {
  if (!ASSET_BASE || !s3Key) return null;
  return `${ASSET_BASE.replace(/\/$/, "")}/${s3Key.replace(/^\//, "")}`;
}
